import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Share2, Copy, Check, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ShareDialog({ site, onChange }) {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(!!site.share_enabled);
  const [token, setToken] = useState(site.share_token || "");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = token ? `${window.location.origin}/share/${token}` : "";

  const toggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        const { data } = await api.post(`/sites/${site.id}/share`);
        setToken(data.share_token);
        setEnabled(true);
        toast.success("Öffentlicher Link aktiviert");
        onChange?.({ ...site, share_enabled: true, share_token: data.share_token });
      } else {
        await api.delete(`/sites/${site.id}/share`);
        setEnabled(false);
        toast.success("Link deaktiviert");
        onChange?.({ ...site, share_enabled: false, share_token: null });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link kopiert");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Konnte Link nicht kopieren");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="share-button" variant="outline" className="border-[#1A2F24] text-[#1A2F24] hover:bg-[#F1EFE7]">
          <Share2 size={16} className="mr-2" /> Teilen
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles size={18} className="text-[#C05A44]" /> Öffentlich teilen
          </DialogTitle>
          <DialogDescription>
            Aktivieren Sie einen öffentlichen Link für diesen Standortbericht. Endkunden können den Bericht ansehen — ohne sich anzumelden. Eine perfekte Visitenkarte für Ihre Arbeit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between bg-[#F1EFE7] border border-[#E5E2D9] rounded-lg p-4">
          <div>
            <Label htmlFor="share-toggle" className="font-medium text-[#1A2F24]">Öffentlicher Link</Label>
            <p className="text-xs text-[#5C6B61] mt-0.5">
              {enabled ? "Aktiv — jeder mit Link kann ansehen" : "Deaktiviert — nur Sie sehen den Bericht"}
            </p>
          </div>
          <Switch
            id="share-toggle"
            data-testid="share-toggle-switch"
            checked={enabled}
            disabled={busy}
            onCheckedChange={toggle}
          />
        </div>

        {enabled && token && (
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wide text-[#5C6B61]">Ihr Link</Label>
            <div className="flex gap-2">
              <Input
                data-testid="share-link-input"
                readOnly
                value={shareUrl}
                className="font-mono text-xs bg-[#F9F8F6] border-[#E5E2D9]"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                data-testid="share-copy-button"
                onClick={copy}
                variant="outline"
                className="border-[#1A2F24] text-[#1A2F24] shrink-0"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </Button>
            </div>
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-open-link"
              className="inline-flex items-center gap-1.5 text-xs text-[#C05A44] hover:underline font-medium"
            >
              In neuem Tab öffnen <ExternalLink size={12} />
            </a>
            <div className="bg-[#FDF7E8] border border-[#F0E5C0] rounded-lg p-3 text-xs text-[#5C6B61]">
              💡 <strong>Tipp:</strong> Senden Sie diesen Link zusammen mit Ihrem Angebot an Kunden. Er sieht den kompletten Standortbericht und Ihre Firma als professionellen Absender.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
